import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { getTimetableRun, listClasses, listSubjects, listStaff, getPeriodTimes, getStaffBusyWeek, getPeriodNotes, getClassSubjectsByWeek, getWeekSubjectPlan, addManualLock, deleteManualLock, TEACHER_DEPARTMENT } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";
import { openPrintWindow } from "./thiKhaoSatPrint";
import { periodShadeIndex } from "./PeriodShadeStyle";
import HappySchoolCellSubstituteDialog from "./HappySchoolCellSubstituteDialog";
import HappySchoolCellAssignDialog from "./HappySchoolCellAssignDialog";

// Webview nhúng trong Zalo/Messenger/Instagram/Line/WeChat CHỈ chặn tải file
// (Blob download) trên iOS (WKWebView giới hạn) — Android webview vẫn tải
// bình thường qua Download Manager của hệ thống nên không cần chặn.
function isBlockedInAppBrowser() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isInApp = /Zalo|FBAN|FBAV|Instagram|Line\/|MicroMessenger/i.test(ua);
  return isIOS && isInApp;
}
function downloadWorkbook(wb, fileName) {
  if (isBlockedInAppBrowser()) {
    window.alert(
      "Trình duyệt trong ứng dụng (Zalo/Messenger/...) trên iPhone không cho phép tải file.\n\n" +
      "Hãy bấm nút menu (⋮ hoặc ...) ở góc trên, chọn \"Mở bằng trình duyệt\" (Safari), rồi bấm Xuất Excel lại."
    );
    return;
  }
  const wbArray = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
// Khối từ tên lớp (vd "12A3" -> "12").
const khoiOfClassName = (name) => (String(name).match(/^(\d+)/) || [])[1] || "";

const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];

export function buildSessions(pt) {
  let cur = 0;
  return (pt && pt.sessions ? pt.sessions : []).map((s) => {
    const periods = (s.periods || []).map((time, i) => ({ global: cur + i, idx: i, time }));
    cur += (s.periods || []).length;
    return { key: s.key, label: s.label, periods };
  });
}
// "d_S"/"d_C" (cả buổi bận) hoặc "d_<tiết toàn ngày>" -> [[day, tiết toàn ngày], ...]
export function busyKeyToPairs(key, sessions) {
  const [dayStr, rest] = String(key).split("_");
  const day = Number(dayStr);
  if (Number.isNaN(day)) return [];
  const bySessionKey = sessions.find((s) => s.key === rest);
  if (bySessionKey) return bySessionKey.periods.map((p) => [day, p.global]);
  const period = Number(rest);
  return Number.isNaN(period) ? [] : [[day, period]];
}

// "TKB Giáo viên — Toàn trường": TKB của TỪNG GV xếp liên tiếp trên 1 trang
// dài, thay vì phải chọn từng người qua combobox — dùng để admin lướt/soát
// toàn bộ 1 lần thay vì bấm từng người.
// lockEdit (22/10/2026): ở Case xếp tay — nhấn đúp ô mở đúng form "Xử lý tiết" / "Điền ô trống" như TKB GV toàn trường,
// nhưng lưu vào Ô XẾP TAY (thêm / đổi / xoá lock) thay vì sửa bản TKB. onLocksChanged: báo trang cha tải lại locks.
const HappySchoolAllTeachersView = ({ runId, weekId, onlyTeacherIds, onlyLockedKeys, locks, canEdit = false, lockEdit = false, onLocksChanged }) => {
  const schoolId = getActiveSchoolId();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [run, setRun] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [busyWeek, setBusyWeek] = useState({});
  const [pccmWeek, setPccmWeek] = useState({}); // PCCM hiệu lực của tuần (đã gộp) — GV chính từng lớp-môn; ai dạy khác thì tự ghi "Dạy thay"
  const [weekPlan, setWeekPlan] = useState({}); // Cấu hình môn học tuần: { classId: { subjectId: số tiết } } — ưu tiên hơn PCCM khi lớp có khai
  const [periodNotes, setPeriodNotes] = useState({}); // { classId: { "day_period": {note, teacherId?} } }
  const [subjectFilter, setSubjectFilter] = useState(""); // "" = tất cả bộ môn
  const [reloadKey, setReloadKey] = useState(0); // tăng lên để tải lại bản sau khi admin xử lý 1 ô
  const [assignTarget, setAssignTarget] = useState(null); // {teacherId, day, period} — ô TRỐNG của GV đang mở dialog điền môn+lớp (nhấn đúp, chỉ admin)
  const [subTarget, setSubTarget] = useState(null); // {classId, day, period} — ô đang mở dialog (nhấn đúp, chỉ admin)

  useEffect(() => {
    // Chế độ `locks` (xem trực tiếp các ô đã khoá) không cần grid của bản chạy
    // nào -> không đòi hỏi runId, tránh trắng trang khi tuần chưa có bản nào.
    if (!schoolId || (!runId && !locks)) return;
    setLoading(true);
    setError("");
    Promise.all([
      runId ? getTimetableRun(schoolId, runId) : Promise.resolve(null),
      listClasses(schoolId),
      listSubjects(schoolId),
      listStaff(schoolId, { department: TEACHER_DEPARTMENT }),
      getPeriodTimes(schoolId),
      weekId ? getStaffBusyWeek(schoolId, weekId) : Promise.resolve({}),
      weekId ? getPeriodNotes(schoolId, weekId, runId).catch(() => ({})) : Promise.resolve({}),
      weekId ? getClassSubjectsByWeek(schoolId, weekId).catch(() => ({})) : Promise.resolve({}),
      weekId ? getWeekSubjectPlan(schoolId, weekId).catch(() => ({})) : Promise.resolve({}),
    ])
      .then(([r, c, s, t, pt, bw, pn, pw, wp]) => {
        setPccmWeek((pw && pw.effective) || {});
        setWeekPlan((wp && wp.plan) || wp || {});
        setRun(r);
        setClasses(c);
        setSubjects(s);
        setTeachers(t.slice().sort((a, b) => a.name.localeCompare(b.name, "vi")));
        setSessions(buildSessions(pt));
        setBusyWeek(bw || {});
        setPeriodNotes(pn || {});
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, runId, weekId, locks, reloadKey]);

  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c.name])), [classes]);
  const subjectById = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s.name])), [subjects]);
  // Ai dạy KHÁC GV chính (PCCM) của lớp-môn đó thì tự ghi "Dạy thay" ngay cạnh tên môn. Bỏ HĐTN (Sinh hoạt/Chào cờ do GVCN, không thuộc PCCM).
  const isDayThay = (classId, sid, tid) => {
    if (!tid || !sid) return false;
    const sn = subjectById[sid] || "";
    if (/^HĐTN/.test(sn)) return false;
    const w = pccmWeek && pccmWeek[classId] && pccmWeek[classId][sid];
    let main = (w && w.assignedTeacherId) || "";
    if (!main) { const c = classes.find((x) => x.id === classId); const f = ((c && c.subjects) || []).find((x) => x.subjectId === sid); main = (f && f.assignedTeacherId) || ""; }
    return !!main && main !== tid;
  };
  const runGrid = (run && run.grid) || {};
  // Chế độ sửa xếp tay: lưới để form tra "lớp đang có tiết gì" = bản TKB + đè bằng chính các ô xếp tay hiện tại.
  const grid = useMemo(() => {
    if (!(lockEdit && locks)) return runGrid;
    const g = {};
    Object.entries(runGrid).forEach(([k, v]) => { if (v && !(Array.isArray(v) && v[1])) g[k] = v; }); // giữ nhãn (HN, Nghỉ...), bỏ tiết của bản
    locks.forEach((l) => { g[`${l.classId}|${l.day}|${l.period}`] = [l.subjectId, l.teacherId]; });
    return g;
  }, [runGrid, lockEdit, locks]);
  const lockAt = (classId, day, period) => (locks || []).find((l) => l.classId === classId && Number(l.day) === Number(day) && Number(l.period) === Number(period));
  const saveLockSubstitute = async ({ classId, day, period, mode, newTeacherId, newSubjectId, reason }) => {
    const old = lockAt(classId, day, period);
    if (!old) throw new Error("Ô này không phải ô xếp tay.");
    await deleteManualLock(schoolId, weekId, old.id);
    if (mode === "replace" || mode === "supervise") {
      try {
        await addManualLock(schoolId, weekId, { classId, subjectId: newSubjectId || old.subjectId, day, period, teacherId: newTeacherId, reason: reason || `Sửa xếp tay: ${classById[classId] || ""}` });
      } catch (e) {
        // không thêm được ô mới -> trả lại ô cũ, không để mất tiết
        await addManualLock(schoolId, weekId, { classId: old.classId, subjectId: old.subjectId, day: old.day, period: old.period, teacherId: old.teacherId, reason: old.reason || "" }).catch(() => {});
        onLocksChanged && onLocksChanged();
        throw e;
      }
    }
    onLocksChanged && onLocksChanged();
    return { ok: true };
  };
  const saveLockAssign = async ({ teacherId, classId, subjectId, day, period, reason, overwrite }) => {
    const old = lockAt(classId, day, period);
    if (old) {
      if (!overwrite) throw new Error("Lớp này đã có ô xếp tay ở giờ này.");
      await deleteManualLock(schoolId, weekId, old.id);
    }
    await addManualLock(schoolId, weekId, { classId, subjectId, day, period, teacherId, reason: reason || `Xếp tay: ${classById[classId] || ""}` });
    onLocksChanged && onLocksChanged();
    return { ok: true };
  };

  // teacherId -> "d|p" -> {a: className, b: subjectName} — đánh chỉ mục 1 lần,
  // tránh quét lại toàn bộ grid cho MỖI ô của MỖI GV (chậm khi nhiều GV).
  const cellsByTeacher = useMemo(() => {
    // Có `locks`: dựng thẳng từ chính danh sách ô đã khoá (classId/subjectId
    // nằm sẵn trong lock) — KHÔNG phụ thuộc bản TKB (grid) nào cả, vì xếp tay
    // là hành động độc lập (chỉ liên quan lịch bận GV + lịch nghề/văn hoá),
    // hiện được ngay cả khi chưa có bản CP-SAT nào chạy lại sau khi khoá.
    if (locks) {
      const out = {};
      locks.forEach((l) => {
        out[l.teacherId] = out[l.teacherId] || {};
        const sn = subjectById[l.subjectId] || l.subjectId;
        out[l.teacherId][`${l.day}|${l.period}`] = { a: classById[l.classId] || l.classId, b: sn + (isDayThay(l.classId, l.subjectId, l.teacherId) ? " · Dạy thay" : ""), c: l.classId };
      });
      return out;
    }
    const out = {};
    Object.entries(grid).forEach(([key, val]) => {
      if (!val || val.length < 2) return;
      const [classId, d, p] = key.split("|");
      const tid = val[1];
      // onlyLockedKeys: chỉ giữ đúng các ô đã xếp tay của GV này — bỏ hết
      // phần còn lại của TKB (do CP-SAT tự xếp), khác với onlyTeacherIds (chỉ
      // lọc NGƯỜI, vẫn hiện nguyên TKB đầy đủ của người đó).
      if (onlyLockedKeys && !onlyLockedKeys.has(`${tid}|${d}|${p}`)) return;
      out[tid] = out[tid] || {};
      const sn = subjectById[val[0]] || "";
      out[tid][`${d}|${p}`] = { a: classById[classId] || classId, b: (val[2] === true ? `${sn} (TC)` : sn) + (isDayThay(classId, val[0], tid) ? " · Dạy thay" : ""), c: classId };
    });
    return out;
  }, [grid, classById, subjectById, onlyLockedKeys, locks, pccmWeek, classes]); // eslint-disable-line

  const teachersWithLessons = useMemo(
    () => teachers.filter((t) => cellsByTeacher[t.id] && (!onlyTeacherIds || onlyTeacherIds.has(t.id))),
    [teachers, cellsByTeacher, onlyTeacherIds]
  );

  // Danh sách bộ môn để làm nút lọc — chỉ những môn thực sự có GV đang dạy
  // trong bản này (tránh nút bấm vào trống trơn).
  const subjectFilterOptions = useMemo(() => {
    const idsPresent = new Set();
    teachersWithLessons.forEach((t) => (t.subjectIds || []).forEach((sid) => idsPresent.add(sid)));
    return subjects
      .filter((s) => idsPresent.has(s.id))
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, "vi"));
  }, [subjects, teachersWithLessons]);

  const displayedTeachers = useMemo(
    () => (subjectFilter ? teachersWithLessons.filter((t) => (t.subjectIds || []).includes(subjectFilter)) : teachersWithLessons),
    [teachersWithLessons, subjectFilter]
  );

  // Tách "Tổng N tiết/tuần" của từng GV: tiết văn hoá (theo môn → lớp) và tiết HĐTN (Sinh hoạt/Chào cờ), kèm các lớp-môn
  // GV được phân công trong PCCM tuần này (đã gộp override riêng tuần + Cấu hình môn học) để đối chiếu với số tiết thực xếp.
  const isHdtn = (name) => String(name || "").normalize("NFC").toUpperCase().startsWith("HĐTN");
  const natCmp = (x, y) => String(x).localeCompare(String(y), "vi", { numeric: true });
  const classStatus = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c.status])), [classes]);
  const printTitle = subjectFilter ? `TKB Giáo viên — Tổ ${subjectById[subjectFilter] || ""}` : "TKB Giáo viên — Toàn trường";
  const breakdownOf = (tid) => {
    const vh = {}, hd = {};
    let nVh = 0, nHd = 0;
    Object.values(cellsByTeacher[tid] || {}).forEach((cell) => {
      const name = String(cell.b || "").replace(/\s*·\s*Dạy thay$/, "").replace(/\s*\(TC\)$/, "") || "(không rõ môn)";
      const hdtn = isHdtn(name);
      if (hdtn) nHd++; else nVh++;
      const o = ((hdtn ? hd : vh)[name] = (hdtn ? hd : vh)[name] || { n: 0, classes: {} });
      o.n++;
      o.classes[cell.a] = (o.classes[cell.a] || 0) + 1;
    });
    const fmt = (bucket) => Object.entries(bucket).sort((x, y) => y[1].n - x[1].n).map(([name, o]) => ({
      name, n: o.n,
      classes: Object.entries(o.classes).sort((x, y) => natCmp(x[0], y[0])).map(([cn, k]) => (k > 1 ? `${cn} (${k})` : cn)).join(", "),
    }));
    const pccm = [];
    let nPccm = 0;
    Object.entries(pccmWeek || {}).forEach(([cid, subs]) => {
      if (classStatus[cid] === "giai_tan") return;
      const plan = weekPlan[cid];
      const usePlan = plan && Object.keys(plan).length > 0;
      Object.entries(subs || {}).forEach(([sid, cfg]) => {
        if (!cfg || cfg.assignedTeacherId !== tid) return;
        if (usePlan && plan[sid] == null) return;
        const per = Number(usePlan ? plan[sid] : cfg.periodsPerWeek) || 0;
        if (per <= 0) return;
        pccm.push({ cls: classById[cid] || cid, subj: subjectById[sid] || sid, per });
        nPccm += per;
      });
    });
    pccm.sort((x, y) => natCmp(x.cls, y.cls) || natCmp(x.subj, y.subj));
    return { nVh, nHd, vh: fmt(vh), hd: fmt(hd), pccm, nPccm };
  };
  const breakdownLines = (bd) => {
    const diff = bd.nPccm - bd.nVh;
    return {
      total: `Tổng ${bd.nVh + bd.nHd} tiết = ${bd.nVh} tiết văn hoá + ${bd.nHd} tiết HĐTN`,
      vh: bd.vh.map((x) => `${x.name} ${x.n} tiết [${x.classes}]`).join(" · "),
      hd: bd.hd.map((x) => `${x.name} ${x.n} tiết [${x.classes}]`).join(" · "),
      pccm: `PCCM tuần này (${bd.nPccm} tiết, chưa tính HĐTN): ` + (bd.pccm.length ? bd.pccm.map((x) => `${x.cls} ${x.subj} ${x.per}`).join(" · ") : "(không có lớp nào)"),
      diff: diff === 0 ? "" : diff > 0 ? `Thiếu ${diff} tiết so với PCCM` : `Xếp dư ${-diff} tiết so với PCCM (dạy thay / khác PCCM)`,
    };
  };

  // In TKB (mỗi GV 1 trang ngang): tiêu đề + tách văn hoá/HĐTN/PCCM + bảng 10 tiết × 6 thứ.
  const printTeachers = (list, title) => {
    const esc = (v) => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const pages = list.map((t) => {
      const L = breakdownLines(breakdownOf(t.id));
      const body = sessions.map((ses) => ses.periods.map((p, ti) => `<tr>${ti === 0 ? `<td rowspan="${ses.periods.length}" class="b">${esc(ses.label)}</td>` : ""}<td>Tiết ${ti + 1}</td>${DAYS.map((d, day) => {
        const cell = (cellsByTeacher[t.id] || {})[`${day}|${p.global}`];
        const busy = isBusyAt(t.id, day, p.global);
        const noteKey = `${day}_${p.global}`;
        const notes = [];
        if (cell && cell.c) { const r = (periodNotes[cell.c] || {})[noteKey]; if (r) notes.push(r.note); }
        Object.entries(periodNotes).forEach(([cid, m]) => {
          const r = m && m[noteKey];
          if (r && r.teacherId === t.id && !(cell && cell.c === cid)) notes.push(`${classById[cid] || cid}: ${r.note}`);
        });
        const noteHtml = notes.length ? `<br><small class="n">📝 ${esc(notes.join(" | "))}</small>` : "";
        if (cell) return `<td class="${busy ? "cb" : ""}">${esc(cell.a)}<br><small>${esc(cell.b)}${busy ? " ⚠ Bận" : ""}</small>${noteHtml}</td>`;
        return busy ? `<td class="bz"><small>Bận</small>${noteHtml}</td>` : `<td>${noteHtml}</td>`;
      }).join("")}</tr>`).join("")).join("");
      return `<section><div class="top">${esc(title || "TKB Giáo viên")}</div><h3>TKB GV: ${esc(t.name)}${t.employmentType ? ` (${esc(t.employmentType)})` : ""}</h3>
        <div class="info"><b>${esc(L.total)}</b>${L.vh ? `<br>Văn hoá: ${esc(L.vh)}` : ""}${L.hd ? `<br>HĐTN: ${esc(L.hd)}` : ""}<br>${esc(L.pccm)}${L.diff ? `<br><b class="w">${esc(L.diff)}</b>` : ""}</div>
        <table><thead><tr><th>Buổi</th><th>Tiết</th>${DAYS.map((d) => `<th>${d}</th>`).join("")}</tr></thead><tbody>${body}</tbody></table></section>`;
    }).join("");
    openPrintWindow(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title || "TKB Giáo viên")}</title><style>
@page{size:A4 landscape;margin:10mm}body{font-family:Arial,sans-serif;font-size:12px;color:#111}
section{page-break-after:always}section:last-child{page-break-after:auto}h3{margin:0 0 6px;font-size:16px}.top{color:#555;font-size:11px;margin-bottom:2px}
.info{margin:0 0 8px;line-height:1.5;font-size:12px}.w{color:#b91c1c}
table{border-collapse:collapse;width:100%}th,td{border:1px solid #444;padding:4px 5px;text-align:center;vertical-align:middle}
th{background:#eee}td.b{font-weight:700;background:#f5f5f5}td small{color:#555}td.bz{background:#fee2e2}td.bz small{color:#b91c1c;font-weight:700}td.cb{background:#f3e8ff}td small.n{color:#92400e}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}
</style></head><body>${pages}<script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`);
  };

  const isBusyAt = (teacherId, day, global) => {
    const b = busyWeek[teacherId];
    if (!b) return false;
    return Object.keys(b).some((k) => busyKeyToPairs(k, sessions).some(([d, p]) => d === day && p === global));
  };

  // Danh sách khối đang có lớp trong trường (thường 10/11/12) — để làm cột
  // riêng từng khối, giống bảng PCCM tổng quát vẫn dùng ở trường.
  const khoisPresent = useMemo(() => {
    const set = new Set();
    classes.forEach((c) => { const k = khoiOfClassName(c.name); if (k) set.add(k); });
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [classes]);

  // Xuất "Danh sách tổng quát PCCM" (cả Thỉnh giảng + Cơ hữu): mỗi dòng là 1
  // (GV, bộ môn) — gộp các lớp GV đó dạy đúng môn đó, theo TỪNG KHỐI thành 1
  // cột riêng (vd "12A4+12A7"), kèm tổng số tiết/tuần của (GV, bộ môn) đó
  // trong ĐÚNG bản TKB đang xem (dữ liệu thật đã xếp, không phải PCCM khai báo).
  const exportPccmSummary = () => {
    // teacherId|subjectName -> { classesByKhoi: {khoi: Set(className)}, periods }
    const agg = {};
    Object.entries(cellsByTeacher).forEach(([tid, cells]) => {
      Object.values(cells).forEach(({ a: className, b: subjLabel }) => {
        const subjName = String(subjLabel || "").replace(/\s*\(TC\)$/, "") || "(Không rõ môn)";
        const key = `${tid}|${subjName}`;
        if (!agg[key]) agg[key] = { classesByKhoi: {}, periods: 0 };
        const khoi = khoiOfClassName(className);
        agg[key].classesByKhoi[khoi] = agg[key].classesByKhoi[khoi] || new Set();
        agg[key].classesByKhoi[khoi].add(className);
        agg[key].periods++;
      });
    });

    const rows = Object.entries(agg).map(([key, v]) => {
      const [tid, subjName] = key.split("|");
      const t = teachers.find((x) => x.id === tid);
      return { tid, subjName, teacherName: t ? t.name : tid, employmentType: t ? (t.employmentType || "") : "", ...v };
    });
    rows.sort((a, b) => a.subjName.localeCompare(b.subjName, "vi") || a.teacherName.localeCompare(b.teacherName, "vi"));

    const header = ["STT", "Bộ môn", "Họ và tên GV", "Thỉnh giảng / Cơ hữu", ...khoisPresent.map((k) => `Khối ${k}`), "Tổng tiết/tuần"];
    const aoa = [header];
    rows.forEach((r, i) => {
      const row = [i + 1, r.subjName, r.teacherName, r.employmentType];
      khoisPresent.forEach((k) => row.push(r.classesByKhoi[k] ? Array.from(r.classesByKhoi[k]).sort((a, b) => a.localeCompare(b, "vi", { numeric: true })).join("+") : ""));
      row.push(r.periods);
      aoa.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PCCM tổng quát");
    downloadWorkbook(wb, "pccm_tong_quat.xlsx");
  };

  if (!runId) return <p style={{ color: "#6b7280" }}>Chưa chọn bản xếp TKB.</p>;
  if (loading) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (teachersWithLessons.length === 0) {
    return (
      <p style={{ color: "#6b7280" }}>
        {onlyTeacherIds ? "Chưa có GV nào được xếp tay có tiết trong bản này." : "Chưa có GV nào có tiết trong bản này."}
      </p>
    );
  }

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        {onlyLockedKeys
          ? `Chỉ hiện đúng ${displayedTeachers.length} GV và đúng các Ô ĐÃ XẾP TAY của họ trong bản này (không hiện phần TKB còn lại do CP-SAT tự xếp) — xếp liên tiếp, lướt xuống để soát từng người.`
          : `TKB của ${displayedTeachers.length} GV${subjectFilter ? ` bộ môn ${subjectById[subjectFilter] || ""}` : ` (toàn bộ ${teachersWithLessons.length} GV có tiết)`} trong bản này, xếp liên tiếp — lướt xuống để soát từng người thay vì chọn qua combobox.`}
      </p>
      {!onlyLockedKeys && (
        <button
          type="button"
          onClick={exportPccmSummary}
          style={{ marginBottom: 10, padding: "8px 16px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}
        >
          ⬇ Xuất Excel — Danh sách tổng quát PCCM (Thỉnh giảng + Cơ hữu)
        </button>
      )}
      {!onlyLockedKeys && (
        <button
          type="button"
          onClick={() => printTeachers(displayedTeachers, printTitle)}
          style={{ marginBottom: 10, marginLeft: 8, padding: "8px 16px", background: "#fff", color: "#0f766e", border: "1px solid #0f766e", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}
        >
          🖨 In {subjectFilter ? `tổ ${subjectById[subjectFilter] || ""}` : "toàn trường"} ({displayedTeachers.length} GV)
        </button>
      )}
      <div style={{ marginBottom: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
        <button type="button" onClick={() => setSubjectFilter("")} style={subjectFilter === "" ? subjBtnActive : subjBtn}>Tất cả</button>
        {subjectFilterOptions.map((s) => (
          <button key={s.id} type="button" onClick={() => setSubjectFilter(s.id)} style={subjectFilter === s.id ? subjBtnActive : subjBtn}>
            {s.name}
          </button>
        ))}
      </div>
      <div style={{ marginBottom: 16, padding: "8px 10px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, display: "flex", flexWrap: "wrap", gap: "4px 10px" }}>
        {displayedTeachers.map((t) => (
          <a key={t.id} href={`#gv-${t.id}`} style={{ fontSize: 12, color: "#0f766e", textDecoration: "none" }}>{t.name}</a>
        ))}
      </div>
      {displayedTeachers.map((t) => (
        <div key={t.id} id={`gv-${t.id}`} style={{ marginBottom: 28, scrollMarginTop: 10 }}>
          <h4 style={{ margin: "0 0 8px" }}>
            🧑‍🏫 TKB GV: {t.name}{t.employmentType ? ` (${t.employmentType})` : ""}{" "}
            <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 13 }}>
              — {onlyLockedKeys ? `${Object.keys(cellsByTeacher[t.id] || {}).length} ô xếp tay` : `Tổng ${Object.keys(cellsByTeacher[t.id] || {}).length} tiết/tuần`}
            </span>
            {!onlyLockedKeys && (
              <button type="button" onClick={() => printTeachers([t], printTitle)} style={{ marginLeft: 10, padding: "3px 10px", background: "#fff", color: "#0f766e", border: "1px solid #0f766e", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>🖨 In</button>
            )}
          </h4>
          {!onlyLockedKeys && (() => {
            const bd = breakdownOf(t.id);
            const L = breakdownLines(bd);
            return (
              <div style={{ margin: "0 0 8px", padding: "6px 10px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, lineHeight: 1.6 }}>
                <div><b>Tổng {bd.nVh + bd.nHd} tiết</b> = <b style={{ color: "#1d4ed8" }}>{bd.nVh} tiết văn hoá</b> + <b style={{ color: "#b45309" }}>{bd.nHd} tiết HĐTN</b></div>
                {L.vh && <div><b>Văn hoá:</b> {L.vh}</div>}
                {L.hd && <div><b>HĐTN:</b> {L.hd}</div>}
                <div><b>{L.pccm.split(": ")[0]}:</b> {L.pccm.slice(L.pccm.indexOf(": ") + 2)}{L.diff && <span style={{ color: bd.nPccm > bd.nVh ? "#b91c1c" : "#b45309", fontWeight: 600 }}> — {L.diff}</span>}</div>
              </div>
            );
          })()}
          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ ...thFix(0), minWidth: 60, zIndex: 6 }}>Buổi</th>
                  <th style={{ ...thFix(60), minWidth: 56, zIndex: 6 }}>Tiết</th>
                  {DAYS.map((d) => (<th key={d} style={{ ...th, minWidth: 120 }}>{d}</th>))}
                </tr>
              </thead>
              <tbody>
                {sessions.map((ses, si) =>
                  ses.periods.map((p, ti) => (
                    <tr key={`${si}-${ti}`} data-pi={periodShadeIndex(p.global)}>
                      {ti === 0 && (
                        <td rowSpan={ses.periods.length} style={{ ...td, position: "sticky", left: 0, zIndex: 5, fontWeight: 700, background: "#f9fafb", verticalAlign: "middle", textTransform: "uppercase" }}>{ses.label}</td>
                      )}
                      <td style={{ ...td, position: "sticky", left: 60, zIndex: 5, background: "#fff", whiteSpace: "nowrap" }}>Tiết {ti + 1}<div style={{ color: "#9ca3af", fontSize: 10 }}>{p.time}</div></td>
                      {DAYS.map((d, day) => {
                        const cell = (cellsByTeacher[t.id] || {})[`${day}|${p.global}`] || null;
                        const busy = isBusyAt(t.id, day, p.global);
                        // Đã xếp (do xếp tay/ghim) NHƯNG GV báo bận đúng giờ đó -> tô tím
                        // để dễ soát, phân biệt với ô bình thường (xanh) hay ô trống-bận (đỏ).
                        const conflictBusy = !!cell && busy;
                        // Ghi chú riêng của ô: của lớp đang dạy ở đây, hoặc gắn thẳng với GV này.
                        const noteKey = `${day}_${p.global}`;
                        const noteLines = [];
                        if (cell && cell.c) {
                          const r = (periodNotes[cell.c] || {})[noteKey];
                          if (r) noteLines.push(r.note);
                        }
                        Object.entries(periodNotes).forEach(([cid, m]) => {
                          const r = m && m[noteKey];
                          if (r && r.teacherId === t.id && !(cell && cell.c === cid)) noteLines.push(`${classById[cid] || cid}: ${r.note}`);
                        });
                        const noteBlock = noteLines.length ? (
                          <div style={{ color: "#92400e", fontSize: 11, whiteSpace: "pre-wrap" }}>📝 {noteLines.join(" | ")}</div>
                        ) : null;
                        return (
                          <td
                            key={d}
                            onDoubleClick={lockEdit && locks ? (cell && cell.c ? () => setSubTarget({ classId: cell.c, day, period: p.global }) : !busy ? () => setAssignTarget({ teacherId: t.id, day, period: p.global }) : undefined)
                              : canEdit && !locks && cell && cell.c && grid[`${cell.c}|${day}|${p.global}`] ? () => setSubTarget({ classId: cell.c, day, period: p.global })
                              : canEdit && !locks && !onlyLockedKeys && !cell && !busy && runId ? () => setAssignTarget({ teacherId: t.id, day, period: p.global }) : undefined}
                            title={lockEdit && locks ? (cell ? "Nhấn đúp để đổi GV / đổi môn / xoá ô xếp tay" : !busy ? "Ô trống — nhấn đúp để thêm ô xếp tay (môn + lớp)" : undefined)
                              : canEdit && !locks && cell && cell.c ? "Nhấn đúp để thay GV / để trống / trông lớp" : canEdit && !locks && !onlyLockedKeys && !cell && !busy && runId ? "Ô trống — nhấn đúp để điền môn và lớp" : undefined}
                            style={{ ...td, minWidth: 110, cursor: (lockEdit && locks && (cell || !busy)) || (canEdit && !locks && (cell || (!onlyLockedKeys && !busy && runId))) ? "pointer" : undefined, background: conflictBusy ? "#f3e8ff" : cell ? "#eff6ff" : (noteBlock ? "#fef9c3" : busy ? "#fee2e2" : "#fff") }}
                          >
                            {noteBlock}
                            {cell ? (
                              <>
                                <div>{cell.a}</div>
                                <div style={{ color: conflictBusy ? "#7c3aed" : "#6b7280", fontSize: 11 }}>
                                  {cell.b}{conflictBusy ? " ⚠️ Bận" : ""}
                                </div>
                              </>
                            ) : (busy ? <div style={{ color: "#b91c1c", fontSize: 11 }}>Bận</div> : "")}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {assignTarget && (
        <HappySchoolCellAssignDialog
          schoolId={schoolId}
          runId={runId}
          weekId={weekId}
          target={assignTarget}
          grid={grid}
          classes={classes}
          subjects={subjects}
          teachers={teachers}
          onClose={() => setAssignTarget(null)}
          onSaved={() => { setAssignTarget(null); setReloadKey((k) => k + 1); }}
          saveOverride={lockEdit && locks ? saveLockAssign : undefined}
        />
      )}
      {subTarget && (
        <HappySchoolCellSubstituteDialog
          schoolId={schoolId}
          runId={runId}
          weekId={weekId}
          target={subTarget}
          grid={grid}
          classes={classes}
          subjects={subjects}
          teachers={teachers}
          isBusyAt={isBusyAt}
          periodNotes={periodNotes}
          onClose={() => setSubTarget(null)}
          onSaved={() => { setSubTarget(null); setReloadKey((k) => k + 1); }}
          saveOverride={lockEdit && locks ? saveLockSubstitute : undefined}
        />
      )}
    </div>
  );
};

const th = { border: "1px solid #e5e7eb", padding: "4px 6px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center", position: "sticky" };
const thFix = (left) => ({ ...th, position: "sticky", left, top: 0, zIndex: 4, background: "#f3f4f6" });
const td = { border: "1px solid #e5e7eb", padding: "3px 5px", textAlign: "center", verticalAlign: "top" };
const subjBtn = { padding: "5px 12px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 600 };
const subjBtnActive = { ...subjBtn, background: "#0ea5a3", color: "#fff", borderColor: "#0ea5a3" };

export default HappySchoolAllTeachersView;
